/**
 * The Overdue and Reminder Engine's arithmetic (piece 9; decision 0121): the
 * three modes a rule can be in, business-day counting, when the next send
 * falls, and what ends a chain. The engine is a scheduler that reads rules;
 * the rules are data.
 */
import { t, type StringKey } from '../strings';
import { DAY_MS } from '../zonedTime';

export type ReminderMode = 'event' | 'date' | 'interval';
export type Audience = 'Internal' | 'Customer' | 'Vendor';
export type ChannelKind = 'Email' | 'InApp' | 'Push';
export type StopConditionKind = 'SubjectClosed' | 'Acknowledged' | 'Never';

export interface RuleRow {
  id: string;
  subjectType: string;
  anchorField: string | null;
  offsets: readonly number[];
  intervalDays: number | null;
  intervalSourceField: string | null;
  audience: Audience;
  channel: ChannelKind;
  templateId: string;
  maxSends: number;
  stopCondition: StopConditionKind;
  businessDaysOnly: boolean;
  active: boolean;
}

export const AUDIENCES: readonly Audience[] = ['Internal', 'Customer', 'Vendor'];
export const CHANNELS: readonly ChannelKind[] = ['Email', 'InApp', 'Push'];
export const STOP_CONDITIONS: readonly StopConditionKind[] = ['SubjectClosed', 'Acknowledged', 'Never'];

/** intervalDays says interval; else an anchor says date; else the rule fires once on the subject event. */
export function reminderMode(rule: Pick<RuleRow, 'anchorField' | 'intervalDays' | 'intervalSourceField'>): ReminderMode {
  if (rule.intervalDays !== null || rule.intervalSourceField !== null) return 'interval';
  return rule.anchorField === null ? 'event' : 'date';
}

export class ReminderRuleError extends Error {}

export function assertRuleShape(rule: Pick<RuleRow, 'anchorField' | 'offsets' | 'intervalDays' | 'maxSends' | 'channel'>): void {
  if (rule.anchorField === null && rule.intervalDays === null && rule.offsets.length > 0) throw new ReminderRuleError('an event-mode rule leaves offsets empty');
  if (rule.maxSends < 1) throw new ReminderRuleError('maxSends is at least one');
  if (rule.intervalDays !== null && rule.intervalDays < 1) throw new ReminderRuleError('intervalDays is at least one');
}


function isWeekend(d: Date): boolean {
  const day = d.getUTCDay();
  return day === 0 || day === 6;
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * DAY_MS);
}

/** Weekends are skipped now; holidays skip once a calendar is declared, and the parameter is where it plugs in. */
export function addBusinessDays(date: Date, days: number, isHoliday: (d: Date) => boolean = () => false): Date {
  const step = days < 0 ? -1 : 1;
  let remaining = Math.abs(days);
  let d = new Date(date.getTime());
  while (remaining > 0) {
    d = addDays(d, step);
    if (!isWeekend(d) && !isHoliday(d)) remaining--;
  }
  return d;
}

export function offsetDate(anchor: Date, offsetDays: number, businessDaysOnly: boolean, isHoliday?: (d: Date) => boolean): Date {
  return businessDaysOnly ? addBusinessDays(anchor, offsetDays, isHoliday) : addDays(anchor, offsetDays);
}

export interface ChainFacts {
  sendsSoFar: number;
  subjectClosed: boolean;
  acknowledged: boolean;
}

/** The subject reaching a closed state ends a chain, so does a person acting on it, and Never leaves maxSends as the only stop. */
export function chainStopped(rule: Pick<RuleRow, 'maxSends' | 'stopCondition'>, facts: ChainFacts): boolean {
  if (facts.sendsSoFar >= rule.maxSends) return true;
  if (rule.stopCondition === 'SubjectClosed') return facts.subjectClosed;
  if (rule.stopCondition === 'Acknowledged') return facts.acknowledged;
  return false;
}

/** A chain that reaches maxSends with its stop condition never cleared escalates to a named human rather than sending forever. */
export function escalationDue(rule: Pick<RuleRow, 'maxSends' | 'stopCondition'>, facts: ChainFacts): boolean {
  if (facts.sendsSoFar < rule.maxSends) return false;
  if (rule.stopCondition === 'SubjectClosed') return !facts.subjectClosed;
  if (rule.stopCondition === 'Acknowledged') return !facts.acknowledged;
  return true;
}

export interface NextSendInput extends ChainFacts {
  anchor: Date | null;
  lastSentAt: Date | null;
  /** The event time for an event-mode rule. */
  eventAt: Date | null;
  /** The period the subject row names through intervalSourceField, overriding intervalDays. */
  intervalFromSubject: number | null;
  isHoliday?: (d: Date) => boolean;
}

/** The moment the next send falls due, or null when nothing more is due. */
export function nextSendAt(rule: RuleRow, input: NextSendInput): Date | null {
  if (!rule.active || chainStopped(rule, input)) return null;
  const mode = reminderMode(rule);
  if (mode === 'event') return input.sendsSoFar === 0 ? input.eventAt : null;
  if (mode === 'date') {
    if (input.anchor === null) return null;
    const offset = rule.offsets[input.sendsSoFar];
    return offset === undefined ? null : offsetDate(input.anchor, offset, rule.businessDaysOnly, input.isHoliday);
  }
  const period = input.intervalFromSubject ?? rule.intervalDays;
  if (period === null || period < 1) return null;
  const from = input.lastSentAt ?? input.anchor ?? input.eventAt;
  if (from === null) return null;
  return offsetDate(from, period, rule.businessDaysOnly, input.isHoliday);
}

export function sendDue(rule: RuleRow, input: NextSendInput, now: Date): boolean {
  const at = nextSendAt(rule, input);
  return at !== null && at.getTime() <= now.getTime();
}

/** A path through at most one declared parent to the field holding the subject's own period, such as rfqId.chaseEveryWorkingDays. */
export function resolveIntervalSource(path: string, subject: Readonly<Record<string, unknown>>, parentOf: (key: string, id: string) => Readonly<Record<string, unknown>> | null): number | null {
  const parts = path.split('.').filter(Boolean);
  if (parts.length === 0 || parts.length > 2) throw new ReminderRuleError('intervalSourceField resolves through at most one declared parent');
  if (parts.length === 1) {
    const v = subject[parts[0]!];
    return typeof v === 'number' && v >= 1 ? v : null;
  }
  const [parentKey, field] = parts as [string, string];
  const id = subject[parentKey];
  if (typeof id !== 'string') return null;
  const parent = parentOf(parentKey, id);
  const v = parent?.[field];
  return typeof v === 'number' && v >= 1 ? v : null;
}

/** An InApp send raises a ChecklistItem; Email sends; Push is configured and not sent until Mobile app lands. */
export type Delivery = 'send' | 'raiseItem' | 'holdForPush';

export function deliveryFor(channel: ChannelKind): Delivery {
  if (channel === 'Email') return 'send';
  if (channel === 'InApp') return 'raiseItem';
  return 'holdForPush';
}

/** The two event-mode subjects this piece registers; later pieces register theirs beside them. */
export const REMINDER_SUBJECTS = ['OperationFailure', 'FileVersionBlocked'] as const;
export type ReminderSubject = (typeof REMINDER_SUBJECTS)[number];

/**
 * 🔨 A REMINDER RULE'S SUBJECT, AUDIENCE, CHANNEL AND STOP CONDITION ARE KEYS,
 * NEVER LABELS (decision 0483, applying decision 0472). The Notifications
 * screen drew all four as they are spelled in the code — `FileVersionBlocked`,
 * `RFQVendorResponse`, `SubjectClosed`, `InApp` — which is exactly where 0472
 * said the sweep it drove would not reach, because that sweep read messages and
 * these are labels.
 *
 * 🔨 The subject list is open: a piece registers its own subject when it ships,
 * so this map carries the ones that exist and falls back to the key for one it
 * has not met. A missing label then reads wrong on the screen rather than
 * drawing nothing.
 */
export const REMINDER_SUBJECT_LABELS: Readonly<Record<string, StringKey>> = {
  OperationFailure: 'reminders.subject.operationFailure',
  FileVersionBlocked: 'reminders.subject.fileVersionBlocked',
  RFQVendorResponse: 'reminders.subject.rfqVendorResponse',
};

export const REMINDER_AUDIENCE_LABELS: Readonly<Record<Audience, StringKey>> = {
  Internal: 'reminders.audience.internal',
  Customer: 'reminders.audience.customer',
  Vendor: 'reminders.audience.vendor',
};

export const REMINDER_CHANNEL_LABELS: Readonly<Record<ChannelKind, StringKey>> = {
  Email: 'reminders.channel.email',
  InApp: 'reminders.channel.inApp',
  Push: 'reminders.channel.push',
};

/** 🔨 Each says when the chasing stops, in the words a person would use for it. */
export const REMINDER_STOP_LABELS: Readonly<Record<StopConditionKind, StringKey>> = {
  SubjectClosed: 'reminders.stop.subjectClosed',
  Acknowledged: 'reminders.stop.acknowledged',
  Never: 'reminders.stop.never',
};

/** What the card calls this reminder; the key itself where nothing has named it. */
export function reminderSubjectLabel(subjectType: string): string {
  const key = REMINDER_SUBJECT_LABELS[subjectType];
  return key ? t(key) : subjectType;
}

/** The one line under the label saying what the rule chases and who it reaches. */
export function reminderRuleSummary(audience: Audience, channel: ChannelKind): string {
  return `${t(REMINDER_AUDIENCE_LABELS[audience] ?? 'reminders.audience.internal')} · ${t(REMINDER_CHANNEL_LABELS[channel] ?? 'reminders.channel.email')}`;
}

/** When the chasing stops, as a person would say it. */
export function reminderStopLabel(stop: string): string {
  const key = REMINDER_STOP_LABELS[stop as StopConditionKind];
  return key ? t(key) : stop;
}
